import { LocalContentProps } from "../types";
import { useQuery } from "@tanstack/react-query";
import errorTracker from "~/utils/errorTracker";

const GOOGLE_ROUTES_ENDPOINT = import.meta.env
  .VITE_GOOGLE_MAPS_API_ENDPOINT as string;

type Location = {
  lat: number | undefined;
  lng: number | undefined;
};

export interface GoogleLocation {
  location: {
    latLng: {
      latitude: number;
      longitude: number;
    };
  };
}

export interface GoogleComputeRouteReponse {
  routes: [
    {
      viewport: {
        low: {
          latitude: number;
          longitude: number;
        };
        high: {
          latitude: number;
          longitude: number;
        };
      };
      localizedValues: {
        distance: {
          text: string;
        };
        duration: {
          text: string;
        };
      };
      polyline: {
        encodedPolyline: string;
      };
    }
  ];
}

export type GoogleTravelMode =
  | "TRAVEL_MODE_UNSPECIFIED"
  | "DRIVE"
  | "BICYCLE"
  | "WALK"
  | "TWO_WHEELER"
  | "TRANSIT";

type GoogleDistanceUnits = "UNITS_UNSPECIFIED" | "METRIC" | "IMPERIAL";

type GooglePolylineEncoding =
  | "POLYLINE_ENCODING_UNSPECIFIED"
  | "ENCODED_POLYLINE"
  | "GEO_JSON_LINESTRING";

export const formatOrigin = (
  sdkState: Partial<
    Pick<LocalContentProps, "options" | "apiKey" | "globalOptions">
  >
) => {
  if (sdkState?.options?.lat) {
    return {
      location: {
        latLng: {
          latitude: sdkState?.options?.lat,
          longitude: sdkState?.options?.lng,
        },
      },
    };
  }

  return null;
};

export const formatDestination = (dest: Location | undefined) => {
  if (dest?.lat && dest?.lng) {
    return {
      location: {
        latLng: {
          latitude: dest.lat,
          longitude: dest.lng,
        },
      },
    };
  }

  return null;
};

export function useGoogleComputeRoute({
  travelMode,
  origin,
  destination,
  units,
  polylineEncoding,
}: {
  travelMode: GoogleTravelMode;
  origin: GoogleLocation | null;
  destination: GoogleLocation | null;
  units: GoogleDistanceUnits;
  polylineEncoding: GooglePolylineEncoding;
}) {
  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string,
      "X-Goog-FieldMask": "*",
    },
    body: JSON.stringify({
      travelMode,
      origin,
      destination,
      units,
      polylineEncoding,
    }),
  };

  const fetchRoutes = (): Promise<GoogleComputeRouteReponse> =>
    fetch(GOOGLE_ROUTES_ENDPOINT, options)
      .then((response) => response.json())
      .catch((error) => {
        errorTracker.captureException(error);
      });

  return useQuery(["useGoogleComputeRoute", options], fetchRoutes, {
    enabled: !!options && !!destination && !!origin,
  });
}
